import React from 'react';
import { Target, Heart, Compass, Users, Shield, Award, TrendingUp, CheckCircle, Zap, Clock, Globe, FileCheck, Scale, Lock } from 'lucide-react';
import TestimonialsSlider from '../components/TestimonialsSlider';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="About File In Canada - Canadian Trademark Experts"
        description="Learn about File In Canada, Canada's leading trademark registration service. Expert CIPO specialists with 98% success rate protecting Canadian brands nationwide."
        keywords="about File In Canada, Canadian trademark experts, CIPO specialists, trademark registration Canada, brand protection services"
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33"
            alt="Professional legal team"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-900/90 to-slate-900/95"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-teal-600/30 backdrop-blur-sm rounded-full px-6 py-3 mb-8 border border-teal-400/30">
              <Scale className="w-5 h-5 text-emerald-400 mr-2" />
              <span className="text-white font-bold">Canada's Premier CIPO Trademark Specialists</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">File In Canada</span>
            </h1>
            <p className="text-2xl text-slate-200 leading-relaxed font-light">
              Protecting Canadian brands through expert trademark registration and unmatched CIPO expertise
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-8 bg-gradient-to-r from-blue-600 to-slate-700 rounded-3xl opacity-20 blur-3xl"></div>
              <img
                src="https://images.pexels.com/photos/5669619/pexels-photo-5669619.jpeg"
                alt="Legal scales representing justice"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-white"
              />
              <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl shadow-2xl p-8 max-w-sm border-4 border-white">
                <div className="flex items-center space-x-4 mb-3">
                  <div className="bg-emerald-400 rounded-xl p-3">
                    <Shield className="w-7 h-7 text-slate-900" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-white">650+</div>
                    <div className="text-sm text-teal-200 font-semibold">Brands Protected</div>
                  </div>
                </div>
                <p className="text-sm text-teal-100">Trusted by Canadian businesses nationwide</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-teal-600 text-white rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
                Our Mission
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">Empowering Canadian Business Owners</h2>
              <p className="text-xl text-slate-600 leading-relaxed mb-6">
                At File In Canada, we exist to make professional trademark protection accessible to every Canadian entrepreneur. Your brand is your most valuable asset—we ensure it's protected with ironclad legal rights.
              </p>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                Through deep CIPO expertise and unwavering commitment to excellence, we deliver trademark registration services that combine speed, precision, and maximum protection.
              </p>
              <div className="bg-white rounded-2xl p-8 border-4 border-teal-600/20">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-5xl font-bold text-teal-700 mb-2">98%</div>
                    <div className="text-slate-600 font-bold">Success Rate</div>
                  </div>
                  <div>
                    <div className="text-5xl font-bold text-teal-700 mb-2">24hr</div>
                    <div className="text-slate-600 font-bold">Response Time</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-white0 text-slate-900 rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
                Why File In Canada
              </div>
              <h2 className="text-4xl font-bold mb-6 leading-tight">
                Unmatched<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">Canadian Expertise</span>
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                We don't do generic trademark services. We specialize exclusively in Canadian trademark law, CIPO procedures, and protecting Canadian businesses. This is all we do—and we do it better than anyone.
              </p>
              
              <div className="space-y-5">
                <div className="flex items-start space-x-4 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/20 hover:bg-yellow-400/30 transition-all">
                  <div className="bg-teal-600 rounded-xl p-3 flex-shrink-0">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-2 text-lg">CIPO Masters</h3>
                    <p className="text-slate-300">Deep mastery of every CIPO requirement, procedure, and classification system.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/20 hover:bg-yellow-400/30 transition-all">
                  <div className="bg-teal-600 rounded-xl p-3 flex-shrink-0">
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-2 text-lg">Lightning Speed</h3>
                    <p className="text-slate-300">24-hour response times and streamlined filing processes save you weeks.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/20 hover:bg-yellow-400/30 transition-all">
                  <div className="bg-teal-600 rounded-xl p-3 flex-shrink-0">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-2 text-lg">Total Transparency</h3>
                    <p className="text-slate-300">Clear pricing, honest timelines, zero surprises. What you see is what you get.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl opacity-30 blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c"
                alt="Professional team collaboration"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-slate-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-teal-600 text-white rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
              Our Foundation
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Core Values</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              The principles that drive every decision we make for Canadian businesses
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-gradient-to-br from-teal-600 to-teal-800 rounded-3xl p-10 shadow-2xl text-white border-4 border-teal-400 hover:scale-105 transition-transform duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-center">Client-First</h3>
              <p className="text-teal-100 leading-relaxed text-center text-lg">
                Your success defines our success. We prioritize your needs and work tirelessly to protect your brand with unwavering dedication.
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-10 shadow-2xl text-white border-4 border-cyan-300 hover:scale-105 transition-transform duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Compass className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-center">Transparency</h3>
              <p className="text-cyan-100 leading-relaxed text-center text-lg">
                Complete honesty in pricing, timelines, and communication. No hidden fees, no surprises—just reliable service you can trust.
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl p-10 shadow-2xl text-white border-4 border-slate-500 hover:scale-105 transition-transform duration-300 min-w-[85%] md:min-w-0 snap-center">
              <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Target className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-center">Excellence</h3>
              <p className="text-slate-300 leading-relaxed text-center text-lg">
                We maintain the highest standards in every aspect—from searches to CIPO filing. Excellence is our baseline, not our goal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-8 bg-gradient-to-r from-blue-600 to-slate-700 rounded-3xl opacity-20 blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33"
                alt="Professional consultation"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-white"
              />
            </div>

            <div>
              <div className="inline-block bg-teal-600 text-white rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
                Our Process
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">How We Protect Your Brand</h2>
              <p className="text-xl text-slate-600 leading-relaxed mb-10">
                Our proven three-step process ensures maximum success rates and minimal stress for Canadian businesses.
              </p>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-5">
                  <div className="bg-gradient-to-br from-teal-600 to-teal-800 text-white w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-2xl flex-shrink-0 shadow-xl">
                    1
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Deep Assessment</h3>
                    <p className="text-slate-600 leading-relaxed text-lg">
                      Comprehensive CIPO database searches ensure your trademark is unique and registrable across Canada.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-5">
                  <div className="bg-gradient-to-br from-teal-600 to-teal-800 text-white w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-2xl flex-shrink-0 shadow-xl">
                    2
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Expert Filing</h3>
                    <p className="text-slate-600 leading-relaxed text-lg">
                      Meticulous preparation of your application with strategic classification for maximum protection.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-5">
                  <div className="bg-gradient-to-br from-teal-600 to-teal-800 text-white w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-2xl flex-shrink-0 shadow-xl">
                    3
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Ongoing Protection</h3>
                    <p className="text-slate-600 leading-relaxed text-lg">
                      Continuous monitoring and expert response to any CIPO actions until your trademark is secured.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Canadian Coverage */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-red-600 text-white rounded-full px-6 py-2 mb-6 text-sm font-bold uppercase tracking-wider">
                Coast to Coast
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Protecting All of Canada
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                From Vancouver to Halifax, from Toronto to Whitehorse—we protect Canadian brands in every province and territory with expert CIPO registration.
              </p>
              
              <div className="bg-gradient-to-br from-teal-50 to-slate-100 rounded-3xl p-8 mb-8 border-4 border-red-200">
                <h3 className="font-bold text-slate-900 mb-6 text-2xl flex items-center">
                  <Globe className="w-7 h-7 text-red-600 mr-3" />
                  Complete National Coverage:
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">British Columbia</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">Alberta</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">Saskatchewan</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">Manitoba</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">Ontario</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">Quebec</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">All Atlantic</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-slate-700 font-semibold">All Territories</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-teal-600 text-white rounded-2xl p-6">
                <TrendingUp className="w-7 h-7 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-xl mb-2">One Registration. Nationwide Protection.</h4>
                  <p className="text-teal-100">A single CIPO trademark protects your brand across all Canadian provinces and territories.</p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-3xl opacity-20 blur-3xl"></div>
              <img
                src="https://images.pexels.com/photos/2916828/pexels-photo-2916828.jpeg"
                alt="Canadian flag"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-white"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              Trusted by Canadian Businesses
            </h2>
            <p className="text-2xl text-slate-300">
              Our results speak louder than words
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-6xl font-bold text-emerald-400 mb-3">650+</div>
              <div className="text-slate-200 font-semibold text-lg">Trademarks Filed</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-6xl font-bold text-emerald-400 mb-3">98%</div>
              <div className="text-slate-200 font-semibold text-lg">Approval Rate</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-6xl font-bold text-emerald-400 mb-3">400+</div>
              <div className="text-slate-200 font-semibold text-lg">Happy Clients</div>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
              <div className="text-6xl font-bold text-emerald-400 mb-3">10+</div>
              <div className="text-slate-200 font-semibold text-lg">Provinces Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSlider />
    </div>
  );
};

export default About;